import type { CrowdexCollaboration, CrowdexUIMessage } from '@crowdex/agent-contracts';
import { useCallback, useEffect } from 'react';
import { useStore } from 'zustand';
import type { ExecutionMode } from '@/components/worktree-controls';
import { type LocalSavedThread, useServerMessageRevision } from '@/hooks/use-message-sync';
import {
  firstMessageDifference,
  recoverPersistedResponses,
} from '@/lib/conversations/message-sync';
import { syncedWorktree } from '@/lib/desktop-shell/conversation-worktree';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import type { NativeAgent } from '@/lib/native-desktop';

type PersistedThread = LocalSavedThread;

/**
 * Keeps the active transcript cached and marked as read, and follows what the
 * server persisted for it (turns are persisted by the server they go through).
 */
export function useConversationPersistence(options: {
  store: DesktopShellStore;
  preview: boolean;
  hydrationReady: boolean;
  serverPersisted?: boolean;
  serverPersistenceSettled?: boolean;
  chatId: string;
  title: string;
  workspace: string | null;
  agent: NativeAgent | null;
  collaboration: CrowdexCollaboration;
  executionMode: ExecutionMode;
  modelId: string | null;
  messages: readonly CrowdexUIMessage[];
  setMessages?: (updater: (messages: CrowdexUIMessage[]) => CrowdexUIMessage[]) => void;
}): { syncError: string | null } {
  const {
    store,
    preview,
    hydrationReady,
    serverPersisted = false,
    serverPersistenceSettled = true,
    chatId,
    title,
    workspace,
    agent,
    collaboration,
    executionMode,
    modelId,
    messages,
    setMessages,
  } = options;
  // This hook writes to the shell store from effects. Subscribing to the whole
  // store makes each of those writes synchronously render the hook again and
  // can turn streaming message updates into a React update loop. Subscribe
  // only to the durable state that actually drives persistence.
  const restoredMessageCount = useStore(store, (state) => state.restoredMessageCount);
  const messageRevision = useStore(store, (state) => state.messageRevision);
  const syncedThreads = useStore(store, (state) => state.syncedThreads);
  const setConversationCache = useStore(store, (state) => state.setConversationCache);
  const setMessageRevision = useStore(store, (state) => state.setMessageRevision);
  const setRestoredMessageCount = useStore(store, (state) => state.setRestoredMessageCount);
  const setSyncedThreads = useStore(store, (state) => state.setSyncedThreads);
  const setSyncedWorkspaces = useStore(store, (state) => state.setSyncedWorkspaces);
  useEffect(() => {
    if (preview || !hydrationReady) return;
    setConversationCache((current) => {
      const previous = current[chatId];
      const messageChanged =
        previous === undefined || firstMessageDifference(previous.messages, messages) !== null;
      const persisted = syncedThreads.find((thread) => thread.id === chatId);
      const restoredUpdatedAt =
        persisted === undefined ? null : Date.parse(persisted.lastMessageAt ?? persisted.updatedAt);
      const agentId = agent?.id;
      if (
        previous !== undefined &&
        !messageChanged &&
        previous.agentId === agentId &&
        previous.title === title &&
        previous.workspaceRoot === workspace &&
        previous.collaboration === collaboration &&
        previous.modelId === (modelId ?? undefined) &&
        previous.restoredMessageCount === restoredMessageCount &&
        previous.messageRevision === messageRevision
      ) {
        return current;
      }
      const updatedAt =
        previous === undefined && restoredMessageCount !== null && restoredUpdatedAt !== null
          ? restoredUpdatedAt
          : messageChanged
            ? Date.now()
            : previous.updatedAt;
      return {
        ...current,
        [chatId]: {
          id: chatId,
          ...(agentId === undefined ? {} : { agentId }),
          title,
          runtime:
            executionMode === 'cloud'
              ? 'cloud'
              : executionMode === 'worktree'
                ? 'worktree'
                : 'local',
          workspaceRoot: workspace,
          collaboration,
          ...(modelId === null ? {} : { modelId }),
          messages: [...messages],
          updatedAt,
          restoredMessageCount,
          messageRevision,
        },
      };
    });
  }, [
    agent,
    chatId,
    hydrationReady,
    messageRevision,
    messages,
    collaboration,
    modelId,
    preview,
    restoredMessageCount,
    executionMode,
    setConversationCache,
    syncedThreads,
    title,
    workspace,
  ]);

  const rememberSavedThread = useCallback(
    (saved: PersistedThread) => {
      setMessageRevision(saved.messageRevision);
      setRestoredMessageCount(saved.messageCount);
      const updatedAt = new Date().toISOString();
      const { workspaceName, worktree, ...rest } = saved;
      const thread = { ...rest, worktree: syncedWorktree(worktree) };
      setSyncedThreads((current) => {
        const index = current.findIndex((entry) => entry.id === thread.id);
        if (index === -1) return [{ ...thread, updatedAt }, ...current];
        return current.map((entry, entryIndex) =>
          entryIndex === index ? { ...entry, ...thread, updatedAt } : entry,
        );
      });
      setSyncedWorkspaces((current) => {
        // The server's record: its folder as stored, and its name (a worktree's is its
        // repository's, not its folder's).
        const savedWorkspace = {
          id: saved.workspaceId,
          ...(saved.logicalWorkspaceId === null
            ? {}
            : { logicalWorkspaceId: saved.logicalWorkspaceId }),
          registered: true,
          availableOnDevice: true,
          runtime: saved.runtime,
          rootPath: saved.workspaceRoot,
          name: workspaceName,
          sourceRoot: worktree?.path === saved.workspaceRoot ? worktree.sourceRoot : null,
        };
        const index = current.findIndex((entry) => entry.id === saved.workspaceId);
        if (index === -1) return [...current, savedWorkspace];
        return current.map((entry, entryIndex) =>
          entryIndex === index ? { ...entry, ...savedWorkspace } : entry,
        );
      });
    },
    [setMessageRevision, setRestoredMessageCount, setSyncedThreads, setSyncedWorkspaces],
  );

  const rememberServerRevision = useCallback(
    (saved: { revision: number; messageCount: number }) => {
      setMessageRevision(saved.revision);
      setRestoredMessageCount(saved.messageCount);
    },
    [setMessageRevision, setRestoredMessageCount],
  );
  const recoverMessages = useCallback(
    (saved: readonly CrowdexUIMessage[]) => {
      setMessages?.((current) => recoverPersistedResponses(current, saved));
    },
    [setMessages],
  );
  const serverSyncError = useServerMessageRevision({
    enabled: serverPersisted && hydrationReady && !preview,
    settled: serverPersistenceSettled,
    threadId: chatId,
    messageCount: messages.length,
    source: executionMode === 'cloud' ? 'cloud' : 'local',
    onLoaded: rememberServerRevision,
    onMessagesLoaded: recoverMessages,
    // The server recorded the conversation and its workspace; the sidebar follows.
    onLocalSaved: rememberSavedThread,
  });

  return { syncError: serverSyncError };
}
